import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll manager: top on a new navigation, previous position on Back/Forward.
 *
 * It used to scroll to 0 on every pathname change, which broke the browser's
 * Back button — you'd scroll to the footer, open Imprint, come back, and land
 * somewhere arbitrary. Two things were fighting:
 *
 *  1. This component scrolled to 0 on POP navigations as well as new ones.
 *  2. The browser's own restoration ran against a page that hadn't finished
 *     laying out. Routes are lazy-loaded and content arrives async, so at the
 *     moment of restore the document was far shorter than it had been — the
 *     browser clamps the target to the current maximum scroll, which is why you
 *     landed around the start of the footer instead of the bottom.
 *
 * So we take control (`scrollRestoration = "manual"`) and restore ourselves,
 * retrying across frames until the document is actually tall enough to honour
 * the position.
 *
 * Positions are keyed by path rather than by history key, and kept in
 * sessionStorage, so a restore still works after a reload — a history key is
 * regenerated on reload and would never match.
 */

const STORAGE_KEY = "as_scroll_positions";
/** Give up after this long; a page that never reaches the old height (content
    genuinely removed) should not leave us retrying forever. */
const RESTORE_BUDGET_MS = 1200;

type Positions = Record<string, number>;

function readPositions(): Positions {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") as Positions;
  } catch {
    return {};
  }
}

function writePosition(key: string, value: number): void {
  try {
    const all = readPositions();
    all[key] = value;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* private mode / quota — restoration is a nicety, never break navigation */
  }
}

export const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const locationKey = `${pathname}${search}`;
  const keyRef = useRef(locationKey);
  keyRef.current = locationKey;

  // Own the restoration so the browser doesn't race us with a clamped guess.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Continuously remember where we are, so the position is already saved by the
  // time a link is tapped — there is no reliable "about to navigate" hook.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        writePosition(keyRef.current, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (navigationType !== "POP") {
      // A genuinely new page starts at the top.
      window.scrollTo(0, 0);
      return;
    }

    const target = readPositions()[locationKey];
    if (!target) {
      window.scrollTo(0, 0);
      return;
    }

    let frame = 0;
    let cancelled = false;
    const deadline = performance.now() + RESTORE_BUDGET_MS;

    // If the reader starts scrolling themselves, stop trying to move them.
    const abort = () => {
      cancelled = true;
    };
    window.addEventListener("wheel", abort, { passive: true, once: true });
    window.addEventListener("touchstart", abort, { passive: true, once: true });
    window.addEventListener("keydown", abort, { once: true });

    const attempt = () => {
      if (cancelled) return;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo(0, Math.min(target, Math.max(0, maxScroll)));
      // Keep going until the page has grown enough to seat the old position, or
      // the budget runs out. Without this the first frame wins and we settle
      // wherever the half-rendered page happened to end.
      if (maxScroll < target && performance.now() < deadline) {
        frame = window.requestAnimationFrame(attempt);
      }
    };
    frame = window.requestAnimationFrame(attempt);

    return () => {
      cancelled = true;
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("wheel", abort);
      window.removeEventListener("touchstart", abort);
      window.removeEventListener("keydown", abort);
    };
  }, [locationKey, navigationType]);

  return null;
};
